import * as React from 'react'
import { api } from '../../../util/api'
import { useAylaTimeout } from '~/components/shared/AylaTimeout'

export const useAiResponseStream = () => {
  const [aiResponse, setAiResponse] = React.useState('')
  const [thankYouNoteId, setThankYouNoteId] = React.useState(null)
  const [aiStreamComplete, setAiStreamComplete] = React.useState(false)
  const [serverSentEvents, setServerSentEvents] = React.useState(false)
  const [aiStreamEmpty, setAiStreamEmpty] = React.useState(false)
  const { timedOut, resetTimeout, pollFailed, pollSucceeded } = useAylaTimeout({
    waiting: !!thankYouNoteId && !aiStreamComplete && !aiStreamEmpty,
    activity: aiResponse,
  })
  const aiFailed = timedOut || aiStreamEmpty

  React.useEffect(() => {
    if (!serverSentEvents) return
    if (!thankYouNoteId) return
    if (timedOut) return

    const eventURL = `/interview/thank_you_notes/create_ai_sse/${thankYouNoteId}?format=json`
    const eventSource = new EventSource(eventURL)
    let received = false

    eventSource.addEventListener('message', (event) => {
      received = true
      setAiResponse((r) => `${r}${event.data}`)
    })

    eventSource.addEventListener('error', (event) => {
      if (event.eventPhase === EventSource.CLOSED) {
        eventSource.close()
        if (received) {
          setAiStreamComplete(true)
        } else {
          setAiStreamEmpty(true)
        }
      }
    })

    return () => eventSource.close()
  }, [thankYouNoteId, serverSentEvents, timedOut])

  React.useEffect(() => {
    if (serverSentEvents) return
    if (!thankYouNoteId) return
    if (timedOut) return

    const pollingURL = `/interview/thank_you_notes/create_ai_polling/${thankYouNoteId}`

    const interval = setInterval(async () => {
      const response = await api.get(pollingURL).catch(() => null)
      if (!response) {
        pollFailed()
        return
      }
      pollSucceeded()
      const {
        data: { content, completed },
      } = response
      setAiResponse(`${content}`)
      setAiStreamComplete(completed)
      const copyButton = document.getElementById('copy-button')
      const downloadButton = document.getElementById(
        'download-button-container',
      )
      const aylaThankYouNoteText = document.getElementById(
        'ayla-thank-you-note-text',
      )
      const thankYouNoteButtons = document.getElementById(
        'thank-you-note-buttons',
      )
      const continueButton = document.getElementById('continue-button')
      if (
        completed &&
        copyButton &&
        downloadButton &&
        aylaThankYouNoteText &&
        thankYouNoteButtons &&
        continueButton
      ) {
        copyButton.style.display = 'flex'
        downloadButton.style.display = 'block'
        aylaThankYouNoteText.style.display = 'block'
        thankYouNoteButtons.style.display = 'flex'
        continueButton.style.display = 'block'
        clearInterval(interval)
        setAiResponse('')
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [
    thankYouNoteId,
    aiResponse,
    aiStreamComplete,
    serverSentEvents,
    timedOut,
    pollFailed,
    pollSucceeded,
  ])

  const resetAiResponse = React.useCallback(() => {
    setThankYouNoteId(null)
    setAiResponse('')
    setAiStreamComplete(false)
    setAiStreamEmpty(false)
    resetTimeout()
  }, [resetTimeout])

  return {
    aiResponse,
    thankYouNoteId,
    setThankYouNoteId,
    aiStreamComplete,
    setServerSentEvents,
    aiFailed,
    resetAiResponse,
  }
}
